/**
 * MetricStrip — canonical horizontal metric row for presentation heroes.
 *
 * Replaces divergent MetricChip/StatRow/StatChip/MetricChip implementations.
 *
 * Props:
 *   metrics    — {icon, value, label}[]
 *   bordered   — use border between chips (mobile divider style)
 *   compact    — tighter padding
 *   accentColor— highlight color for metric values
 *   style      — container style override
 */
import React, { useState } from 'react';
import { base } from '../tokens/colors';

export function MetricStrip({ metrics, bordered = false, compact = false, accentColor, style }) {
  if (!metrics || metrics.length === 0) return null;
  const valid = metrics.filter(m => m && m.value);
  if (valid.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      gap: bordered ? 0 : 8,
      ...style,
    }}>
      {valid.map((m, i) => (
        <MetricChip
          key={i}
          icon={m.icon}
          value={m.value}
          label={m.label}
          bordered={bordered && i < valid.length - 1}
          compact={compact}
          accentColor={accentColor}
          isLast={i === valid.length - 1}
        />
      ))}
    </div>
  );
}

function MetricChip({ icon, value, label, bordered, compact, accentColor, isLast }) {
  const [hov, setHov] = useState(false);
  const pad = compact ? '8px 4px' : '10px 8px';

  if (bordered) {
    return (
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 3, padding: pad,
        borderRight: !isLast ? `1px solid ${base.border}` : 'none',
      }}>
        {icon && <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>}
        <span style={{
          fontSize: 12, fontWeight: 700, color: base.text,
          textAlign: 'center', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
        }}>{value}</span>
        {label && <span style={{
          fontSize: 9, color: base.textMuted,
          textTransform: 'uppercase', letterSpacing: '0.05em',
          textAlign: 'center', lineHeight: 1.2,
        }}>{label}</span>}
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 3, padding: pad,
        background: hov ? 'rgba(59,130,246,0.06)' : 'rgba(255,255,255,0.025)',
        border: `1px solid ${hov ? 'rgba(59,130,246,0.22)' : base.border}`,
        borderRadius: 10,
        cursor: 'default',
        transition: 'all 0.18s ease',
      }}
    >
      {icon && <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>}
      <span style={{
        fontSize: 12.5, fontWeight: 700,
        color: accentColor && hov ? accentColor : base.text,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%', textAlign: 'center',
      }}>{value}</span>
      {label && <span style={{
        fontSize: 9.5, color: base.textMuted,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        whiteSpace: 'nowrap', textAlign: 'center',
      }}>{label}</span>}
    </div>
  );
}

export default MetricStrip;
